import { useState } from 'react'

import { isZeroQuotePlaceholder } from '@b-stock/fe-core/bstock-next/helpers'
import type {
  OrchestratedAuctionListingDetails,
  OrchestratedOfferingListingDetails,
  OrchestratedQuote,
} from '@b-stock/fe-core/bstock-next/queries'
import { FormattedMessage, showSnackbar } from '@b-stock/fe-core/bstock-react'
import type { OrderResponseDto } from '@b-stock/order-api-client'
import { PaymentStatusEnum } from '@b-stock/order-api-client'
import { ShipmentType } from '@b-stock/shipment-api-client'

import canChangeShipping from '@/buyer-portal/components/features/order/OrderDetailsLayout/canChangeShipping'
import useChangeCarrierModal from '@/buyer-portal/hooks/useChangeCarrierModal'

/**
 * Encapsulates the change-carrier flow shared by the V1 shipping summary and the
 * V2 shipping widget: derives the modal inputs from the listing/quote, computes
 * whether the change affordance should show, owns the open/pending state and the
 * error snackbar, and hands back a ready-to-render modal element. Callers only
 * wire up the trigger and drop `carrierModal` into their tree.
 */
const useChangeCarrier = ({
  listing,
  selectedQuote,
  order,
  hasAutoQuoteError = false,
}: {
  listing:
    | OrchestratedAuctionListingDetails
    | OrchestratedOfferingListingDetails
  selectedQuote?: OrchestratedQuote | null
  order?: OrderResponseDto | null
  // Suppress carrier change while auto-quote generation is erroring
  hasAutoQuoteError?: boolean
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isChanging, setIsChanging] = useState(false)

  const {
    ChangeCarrierModal,
    changeCarrierModalProps,
    hasMultipleCarrierOptions,
    isCarrierChangePending,
    setSelectedCarrier,
  } = useChangeCarrierModal({
    accountId: selectedQuote?.accountId ?? '',
    currentQuote: selectedQuote ?? undefined,
    destinationLocationId: selectedQuote?.destination.id ?? '',
    listingId: listing._id,
    originLocationId: listing.lot.location.id,
    shipmentType: selectedQuote?.shipmentType ?? ShipmentType.Binding,
  })

  const shouldShowCarrierChange =
    !!selectedQuote &&
    selectedQuote.shipmentType === ShipmentType.Binding &&
    (!order || canChangeShipping(order)) &&
    (!order || order.payment?.status !== PaymentStatusEnum.Paid) &&
    !isZeroQuotePlaceholder(selectedQuote) &&
    !hasAutoQuoteError

  const handleCarrierChange = async (quoteId: string) => {
    setIsModalOpen(false)
    setIsChanging(true)
    try {
      await setSelectedCarrier(quoteId)
    } catch {
      showSnackbar({
        content: (
          <FormattedMessage id="ListingShippingSummary.changeCarrier.error" />
        ),
        options: { autoClose: true, type: 'error' },
      })
    } finally {
      setIsChanging(false)
    }
  }

  const carrierModal = isModalOpen ? (
    <ChangeCarrierModal
      {...changeCarrierModalProps}
      onChange={handleCarrierChange}
      onClose={() => setIsModalOpen(false)}
      isCarrierChangePending={isCarrierChangePending}
    />
  ) : null

  return {
    // The quote is a changeable Binding quote (independent of option count)
    shouldShowCarrierChange,
    hasMultipleCarrierOptions,
    isCarrierChanging: isChanging || isCarrierChangePending,
    openCarrierModal: () => setIsModalOpen(true),
    carrierModal,
  }
}

export default useChangeCarrier
